import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { CategoryTreeComponent } from '../category-tree/category-tree.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { units } from '../../../models/unit';
import { BadgeModule } from 'primeng/badge';
import { NgClass, NgIf } from '@angular/common';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { TooltipModule } from 'primeng/tooltip';
import { addById, removeByIds } from '../../../utility/array.utility';
import { MenuItem, MessageService, SharedModule } from 'primeng/api';
import { TagListComponent } from '../../molecules/tag-list/tag-list.component';
import { TagModule } from 'primeng/tag';
import { Indicator } from '../../../models/indicator';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { PoolFilterComponent } from '../../molecules/pool-filter/pool-filter.component';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { Category } from '../../../models/category';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ContextMenuModule } from 'primeng/contextmenu';
import { IndicatorDetailComponent } from '../indicator-detail/indicator-detail.component';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RoutingService } from '../../../services/routing.service';
import { CategoryStore } from '../../../stores/category/category.state';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { DialogModule } from 'primeng/dialog';
import { SidebarModule } from 'primeng/sidebar';
import { DebugComponent } from '../../atoms/debug/debug.component';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
import { TagSummaryPipe } from '../../../pipes/tag-summary.pipe';
@Component({
  selector: 'app-indicator-pool',
  standalone: true,
  imports: [
    CategoryTreeComponent,
    HeaderComponent,
    BadgeModule,
    NgIf,
    PickListModule,
    SharedModule,
    TagListComponent,
    TagModule,
    NgClass,
    PoolFilterComponent,
    DividerModule,
    ButtonModule,
    CheckboxModule,
    ChipModule,
    ScrollPanelModule,
    ContextMenuModule,
    IndicatorDetailComponent,
    OverlayPanelModule,
    ConfirmDialogModule,
    AlertComponent,
    DialogModule,
    SidebarModule,
    DebugComponent,
    TooltipModule,
    PickListHeaderComponent,
    TagSummaryPipe,
  ],
  templateUrl: './indicator-pool.component.html',
  styleUrl: './indicator-pool.component.scss',
})
export class IndicatorPoolComponent implements OnDestroy {
  @ViewChild('op') op!: OverlayPanel;
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-12',
    icon: 'fas fa-pen-ruler',
    headerText: 'Indikatorenvorlagen',
    subtitle:
      'Nutzen Sie diese Indikatorenvorlagen, um sie in Ihr Indikatorenverzeichnis zu übernehmen und anzupassen.',
  };
  protected readonly units = units;
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorStore = inject(IndicatorStore);
  contextMenu: MenuItem[] = [];
  categoryStep = false;
  /** Chosen pool indicators; the order defines the sorting of the copies. */
  readonly selected = signal<Indicator[]>([]);

  /** Filtered pool indicators which are not chosen yet. */
  readonly sourceList = computed(() => {
    const selectedIds = new Set(this.selected().map(i => i._id));
    return this.indicatorStore
      .poolFiltered()
      .filter((indicator: Indicator) => !selectedIds.has(indicator._id));
  });
  indicatorToShow: Indicator | null = null;
  displayIndicatorDetails = false;
  editMode = false;

  constructor(
    private sidebarService: SidebarService,
    protected confirmationDialogService: ConfirmationDialogService,
    protected messageService: MessageService,
    private routingService: RoutingService
  ) {
    this.indicatorStore.findPool(); // load pool into the store
    setTimeout(() => {
      this.sidebarService.setConfiguration(this.sidebarConfig);
    }, 10);
  }

  /**
   * Sets the categoryStep property to true, indicating that the category step is selected for copying.
   *
   * @return {void} This method does not return any value.
   */
  selectForCopy(): void {
    this.categoryStep = true;
  }

  /**
   * Confirms the copying of selected indicators into the specified category.
   * Opens a confirmation dialog and, upon confirmation, performs the copy operation.
   *
   * @param category The target category where the indicators will be copied.
   * @return void
   */
  copyIndicatorConfirmation(category: Category): void {
    this.confirmationDialogService.confirmCopyFromPool(
      () => this.copyIndicators(category),
      `${this.selected().length} Indikatoren in den Ordner "${category.name}" kopieren?`
    );
  }

  /**
   * Copies selected indicators into a new category and updates their properties for insertion.
   * It also resets certain properties, creates children indicators if applicable, and inserts the new indicators into the store.
   *
   * @param {Category} category - The category object to which the indicators will be associated.
   * @return {void} This method does not return any value.
   */
  copyIndicators(category: Category): void {
    const newIndicators: Indicator[] = [];
    let sorting = 0;
    for (const selected of this.selected()) {
      const indicator = { ...selected };

      if (indicator.isSet) {
        indicator['children'] = this.indicatorStore
          .filterPoolByParent(indicator._id)
          .map((child: Indicator) => {
            const childToAdd = { ...child };
            childToAdd.category = category._id;
            childToAdd.pool = false;
            childToAdd.sorting = sorting;
            childToAdd.parent = '';

            // removes props to insert new
            childToAdd._id = '';
            delete childToAdd.user;
            delete childToAdd.commune;

            return childToAdd;
          });
      }

      indicator.category = category._id;
      indicator.pool = false;
      indicator.sorting = sorting;

      // removes props to insert new
      indicator._id = '';
      delete indicator.user;
      delete indicator.commune;
      newIndicators.push(indicator);
      sorting++;
    }

    this.indicatorStore.insertMultiple({ items: newIndicators });
    this.categoryStep = false;
    this.selected.set([]);
    this.routingService.redirectToRoute([
      '/data-backend',
      {
        outlets: {
          overlay: ['indikatoren'],
        },
      },
    ]);
  }

  /**
   * Handles the opening of a context menu based on the provided event and indicator.
   *
   * @param {any} event - The event object associated with the context menu activation.
   * @param {Indicator} indicator - The indicator object providing context for the menu.
   * @return {void} This method does not return a value.
   */
  onContextMenuOpen(event: any, indicator: Indicator): void {
    this.updateContextMenu(indicator, event);
  }

  /**
   * Updates the context menu with options related to the provided indicator.
   *
   * @param {Indicator} indicator - The indicator object for which the context menu is being updated.
   * @param {any} originalEvent - The original event that triggered the context menu update.
   * @return {void} This method does not return a value.
   */
  updateContextMenu(indicator: Indicator, originalEvent: any): void {
    this.contextMenu = [
      //{
      //  label: 'bearbeiten',
      //  icon: 'fas fa-pen',
      //  command: event => this.editIndicator(originalEvent, indicator),
      //},
      {
        label: 'Info',
        icon: 'fas fa-info-circle',
        command: event => this.showIndicatorDetails(originalEvent, indicator),
      },
    ];
  }

  /**
   * Displays the details of a specific indicator based on the provided event and indicator data.
   *
   * @param {any} originalEvent - The original event object triggering the method.
   * @param {Indicator} indicator - The indicator object whose details are to be shown.
   * @return {void} This method does not return a value.
   */
  showIndicatorDetails(originalEvent: any, indicator: Indicator): void {
    this.displayIndicatorDetails = true;
    this.indicatorToShow = indicator;
  }

  /**
   * Edits the specified indicator and sets the application into edit mode.
   *
   * @param {any} originalEvent - The original event that triggered the edit action.
   * @param {Indicator} indicator - The indicator object to be edited.
   * @return {void} Does not return a value.
   */
  editIndicator(originalEvent: any, indicator: Indicator): void {
    this.displayIndicatorDetails = true;
    this.indicatorToShow = indicator;
    this.editMode = true;
    // todo: adds form on dialog, important - on save - the indicator should not get automatic commune in indicatorService.js!
  }

  /**
   * Closes the currently displayed overlay by resetting relevant properties.
   * Updates the state to exit edit mode and clears any visible indicators.
   *
   * @return {void} Does not return a value.
   */
  closeOverlay(): void {
    this.indicatorToShow = null;
    this.displayIndicatorDetails = false;
    this.editMode = false;
  }

  /**
   * Number of children of a pool set.
   *
   * @param {Indicator} set - The pool set.
   * @return {number} The number of children in the pool.
   */
  poolChildCount(set: Indicator): number {
    return this.indicatorStore.filterPoolByParent(set._id).length;
  }

  /**
   * Adds the moved pool indicators to the chosen ones.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved indicators.
   */
  addToSelection(event: PickListMoveToTargetEvent): void {
    this.selected.set(addById(this.selected(), event.items as Indicator[]));
  }

  /**
   * Removes the moved pool indicators from the chosen ones.
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved indicators.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    this.selected.set(
      removeByIds(
        this.selected(),
        (event.items as Indicator[]).map(indicator => indicator._id)
      )
    );
  }

  /**
   * PrimeNG reorders the target array in place - publish it as a new value.
   */
  onTargetReorder(): void {
    this.selected.set([...this.selected()]);
  }

  /**
   * Cancels the current operation by resetting the category step flag
   * and clearing the selected items array.
   *
   * @return {void} No return value.
   */
  cancel(): void {
    this.categoryStep = false;
    this.selected.set([]);
  }

  /**
   * Navigates back to the previous step by resetting the category step state.
   *
   * @return {void} No return value.
   */
  back(): void {
    this.categoryStep = false;
  }

  /**
   * Lifecycle hook that is called when a component or directive is destroyed.
   * This method is typically used to perform clean-up tasks such as unsubscribing from observables,
   * invalidating resources, or clearing configurations related to the instance.
   *
   * In this case, it clears a filter managed by the indicator store.
   *
   * @return {void} No return value.
   */
  ngOnDestroy() {
    this.indicatorStore.clearFilter();
  }
}
